import React, { useEffect, useState, useContext } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { addMonths, format, isSameMonth } from 'date-fns';
import { de } from 'date-fns/locale';
import './Availability.css';
import { LanguageContext } from "../context/LanguageContext";

const API_URL = process.env.REACT_APP_API_URL;

function isValidHttpUrl(string) {
  try { 
    const url = new URL(string); 
    return ['http:', 'https:'].includes(url.protocol); 
  } catch (_) { 
    return false; 
  }
}

// Helper: build full window day-map (3 months) then mark occupied days
function buildAvailabilityMapFromOccupied(occupiedDays = [], baseDate = new Date(), months = 3) {
  const map = {};
  const start = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
  const windowEnd = new Date(start);
  windowEnd.setMonth(windowEnd.getMonth() + months);
  windowEnd.setDate(windowEnd.getDate() - 1);

  for (let d = new Date(start); d.getTime() <= windowEnd.getTime(); d.setDate(d.getDate() + 1)) {
    const key = format(d, 'yyyy-MM-dd');
    map[key] = { status: 'available' };
  }

  if (Array.isArray(occupiedDays)) {
    occupiedDays.forEach(d => {
      if (!d) return;
      map[d] = { status: 'occupied' };
    });
  }

  return map;
}

// Helper: convert server ranges -> map
function rangesToIcalMap(ranges = []) {
  const map = {};
  if (!Array.isArray(ranges)) return map;
  ranges.forEach(r => {
    if (!r || !r.start_date || !r.end_date) return;
    const s = new Date(r.start_date);
    const e = new Date(r.end_date);
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      map[format(d, 'yyyy-MM-dd')] = { status: 'available' };
    }
  });
  return map;
}

// occupiedDays normalization from preview events
function occupiedDaysFromPreviewEvents(events = []) {
  const out = new Set();
  if (!Array.isArray(events)) return [];
  const isMidnightUTC = (d) =>
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0;

  events.forEach(ev => {
    if (!ev || !ev.rawStart) return;
    const start = new Date(ev.rawStart);
    let end = ev.rawEnd ? new Date(ev.rawEnd) : new Date(ev.rawStart);

    if (ev.rawEnd) {
      try {
        const diff = end.getTime() - start.getTime();
        if (isMidnightUTC(end) || diff <= 0 || (diff % (24 * 3600 * 1000) === 0)) {
          end = new Date(end);
          end.setUTCDate(end.getUTCDate() - 1);
        }
      } catch (e) {}
    }

    for (let d = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
         d.getTime() <= Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
         d.setUTCDate(d.getUTCDate() + 1)) {
      out.add(format(d, 'yyyy-MM-dd'));
    }
  });

  return Array.from(out).sort();
}

// Normalize incoming server timestamps to a local "wall-clock" string
function normalizeServerTimestampToLocalWallClock(raw) {
  if (!raw) return raw;
  const s = String(raw).trim();
  const hasTZ = /Z$|[+-]\d{2}(:?\d{2})?$/.test(s);

  if (!hasTZ) {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
    return m ? `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}` : s;
  }

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const Y = d.getUTCFullYear();
  const Mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const D = String(d.getUTCDate()).padStart(2, '0');
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  const ss = String(d.getUTCSeconds()).padStart(2, '0');
  return `${Y}-${Mo}-${D}T${hh}:${mm}:${ss}`;
}

// Format a wall-clock string for local display
function formatLocalFromWallClock(wallClock) {
  if (!wallClock) return '';
  const m = String(wallClock).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/);
  if (m) {
    const [,Y,Mo,D,H,Mi,Se] = m;
    const d = new Date(Number(Y), Number(Mo)-1, Number(D), Number(H), Number(Mi), Number(Se));
    return d.toLocaleString();
  }
  const d = new Date(wallClock);
  if (Number.isNaN(d.getTime())) return String(wallClock);
  return d.toLocaleString();
}

export default function AvailabilityIcalTab({ objectId, onRefresh, setMessage }) {
  const { texts } = useContext(LanguageContext);

  const [icalUrl, setIcalUrl] = useState('');
  const [originalUrl, setOriginalUrl] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [previewError, setPreviewError] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [isTesting, setIsTesting] = useState(false);

  const [baseMonth, setBaseMonth] = useState(new Date());
  const [icalAvailabilityStatus, setIcalAvailabilityStatus] = useState({});
  const [rangeStarts, setRangeStarts] = useState(new Set());
  const [rangeEnds, setRangeEnds] = useState(new Set());
  const [occupiedSet, setOccupiedSet] = useState(new Set());
  const [preview, setPreview] = useState(null);

  // Polling last_sync
  useEffect(() => {
    if (!objectId) return;
    let mounted = true;
    let timer = null;

    async function fetchLastSync() {
      try {
        const res = await fetch(`${API_URL}/object/${objectId}/availability/ical`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
          cache: 'no-cache'
        });
        if (!res.ok) return;
        const data = await res.json();
        const incomingNormalized = normalizeServerTimestampToLocalWallClock(data?.last_synced_at || data?.lastSyncedAt || null);
        if (mounted && incomingNormalized && incomingNormalized !== lastSync) {
          setLastSync(incomingNormalized);
        }
      } catch (e) {}
      finally {
        if (mounted) timer = setTimeout(fetchLastSync, 20000);
      }
    }

    timer = setTimeout(fetchLastSync, 2000);
    return () => { mounted = false; if (timer) clearTimeout(timer); };
  }, [objectId, lastSync]);

  // Initial load
  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!objectId) return;
      setIsLoading(true);
      try {
        const res = await fetch(`${API_URL}/object/${objectId}/availability/ical`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
        });
        if (res.status === 204 || res.status === 404) {
          if (!mounted) return;
          setOriginalUrl(null); 
          setIcalUrl(''); 
          setLastSync(null);
          return;
        }
        if (!res.ok) throw new Error('fetch failed');
        const data = await res.json();
        setOriginalUrl(data.ical_url || null);
        setIcalUrl(data.ical_url || '');
        setLastSync(normalizeServerTimestampToLocalWallClock(data.last_synced_at || data.lastSyncedAt || null));
      } catch (err) {
        console.error('load ical', err);
        setMessage?.(texts.availabilityIcal.errorLoading);
      } finally {
        if (mounted) setIsLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [objectId, setMessage, texts]);

  const handlePrev = () => setBaseMonth(prev => addMonths(prev, -1));
  const handleNext = () => setBaseMonth(prev => addMonths(prev, 1));

  async function handleTestPreview(event) {
    event?.preventDefault?.();
    if (!icalUrl) {
      setPreviewError(texts.availabilityIcal.errorNoUrl);
      return;
    }

    setIsTesting(true);
    setPreviewError(null);
    setPreviewData(null);
    setIcalAvailabilityStatus({});

    try {
      const res = await fetch(`${API_URL}/api/ical/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ icalUrl })
      });

      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        const text = await res.text().catch(()=>'<no body>');
        throw new Error(`Server returned ${res.status}: ${text.slice(0,200)}`);
      }
      if (!ct.includes('application/json')) {
        const text = await res.text().catch(()=>'<no body>');
        throw new Error(`Expected JSON, got ${ct}: ${text.slice(0,200)}`);
      }

      const data = await res.json();
      setPreviewData(data);

      let occupiedArr = Array.isArray(data.occupiedDays) && data.occupiedDays.length ? data.occupiedDays : [];

      if ((!occupiedArr || occupiedArr.length === 0) && Array.isArray(data.events) && data.events.length) {
        occupiedArr = occupiedDaysFromPreviewEvents(data.events);
      }

      const now = new Date();
      const displayBase = new Date(now.getFullYear(), now.getMonth(), 1);
      setBaseMonth(displayBase);

      const dayMap = buildAvailabilityMapFromOccupied(occupiedArr, displayBase, 3);
      setIcalAvailabilityStatus(dayMap);

      setOccupiedSet(new Set(Array.isArray(occupiedArr) ? occupiedArr : []));

      setPreview({
        eventCount: data.meta?.eventsCount ?? (data.events?.length || 0),
        firstEvent: data.events?.[0]?.rawStart,
        lastEvent: data.events ? data.events[data.events.length - 1]?.rawEnd : null
      });

    } catch (err) {
      console.error('Preview error', err);
      setPreviewError(texts.availabilityIcal.errorPreview);
    } finally {
      setIsTesting(false);
    }
  }


  const handleSave = async () => {
    if (!objectId) {
      setMessage?.(texts.availabilityIcal.errorNoObjectId);
      return;
    }

    if (!isValidHttpUrl(icalUrl)) {
      setMessage?.(texts.availabilityIcal.errorInvalidUrl);
      return;
    }

    setIsSaving(true);

    try {
      const resp = await fetch(`${API_URL}/object/${objectId}/availability/ical`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ ical_url: icalUrl }),
      });

      if (!resp.ok) throw new Error('save failed');

      const data = await resp.json();
      const incomingNormalized = normalizeServerTimestampToLocalWallClock(
        data?.last_synced_at || data?.lastSyncedAt || null
      );

      // Update UI
      setOriginalUrl(data.ical_url || icalUrl);
      setPreviewData(null);
      setIcalAvailabilityStatus({});
      setPreviewError(null);

      // Trigger immediate sync (fire & forget)
      fetch(`${API_URL}/object/${objectId}/availability/ical/sync`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ical_link_id: data.id || data.ical_link_id || null })
      }).catch(() => {});

      setMessage?.(texts.availabilityIcal.saved);

      // Recheck last_sync after short delay
      setTimeout(async () => {
        try {
          const r = await fetch(`${API_URL}/object/${objectId}/availability/ical`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('authToken')}`,
              'Cache-Control': 'no-cache'
            }
          });

          if (!r.ok) {
            if (incomingNormalized) setLastSync(incomingNormalized);
            return;
          }

          const refreshed = await r.json();
          const reNorm = normalizeServerTimestampToLocalWallClock(
            refreshed?.last_synced_at || refreshed?.lastSyncedAt || null
          );

          if (reNorm) setLastSync(reNorm);
          else if (incomingNormalized) setLastSync(incomingNormalized);

        } catch {
          if (incomingNormalized) setLastSync(incomingNormalized);
        }
      }, 1200);

      if (typeof onRefresh === 'function') await onRefresh();

    } catch (err) {
      console.error('save error', err);
      setMessage?.(texts.availabilityIcal.errorSaving);
    } finally {
      setIsSaving(false);
      setTimeout(() => setMessage?.(''), 3000);
    }
  };

  const handleRemove = async () => {
    if (!objectId) return;

    setIsSaving(true);

    try {
      const resp = await fetch(`${API_URL}/object/${objectId}/availability/ical`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      });

      const text = await resp.text();
      let parsed;
      try { parsed = text ? JSON.parse(text) : null; }
      catch { parsed = { raw: text }; }

      if (![200, 204].includes(resp.status)) {
        setMessage?.(
          `${texts.availabilityIcal.errorRemove} (${resp.status}): ${parsed?.message || parsed?.raw || 'no details'}`
        );
        return;
      }

      setOriginalUrl(null);
      setIcalUrl('');
      setLastSync(null);
      setIcalAvailabilityStatus({});
      setPreviewData(null);
      setPreviewError(null);

      setMessage?.(texts.availabilityIcal.removed);

      if (typeof onRefresh === 'function') await onRefresh();

    } catch (err) {
      console.error('remove error', err);
      setMessage?.(texts.availabilityIcal.errorRemove);
    } finally {
      setIsSaving(false);
      setTimeout(() => setMessage?.(''), 3000);
    }
  };

  async function handleShowSynced() {
    if (!objectId) return;

    setIsLoading(true);
    setPreviewError(null);

    try {
      const res = await fetch(`${API_URL}/object/${objectId}/availability`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      });

      if (!res.ok) throw new Error('failed to load availabilities');

      const ranges = await res.json();
      const previewMap = rangesToIcalMap(ranges);

      const starts = new Set();
      const ends = new Set();

      ranges.forEach(r => {
        if (!r) return;
        try {
          const s = new Date(r.start_date);
          const e = new Date(r.end_date);
          starts.add(format(s, 'yyyy-MM-dd'));
          ends.add(format(e, 'yyyy-MM-dd'));
        } catch {}
      });

      setIcalAvailabilityStatus(previewMap);
      setRangeStarts(starts);
      setRangeEnds(ends);
      setPreviewData(null);

    } catch (e) {
      console.error('load synced data error', e);
      setPreviewError(texts.availabilityIcal.errorLoadSynced);
    } finally {
      setIsLoading(false);
    }
  }


   // Helper: find first/last available in visible window
  function findWindowFirstLastAvailable(map = {}, baseMonthDate = new Date(), months = 3) {
    const start = new Date(baseMonthDate.getFullYear(), baseMonthDate.getMonth(), 1);
    const end = new Date(start);
    end.setMonth(end.getMonth() + months);
    end.setDate(end.getDate() - 1);

    const keys = Object.keys(map)
      .filter(k => map[k]?.status === 'available')
      .map(k => new Date(k + 'T00:00:00'));

    const inWindow = keys.filter(d => d >= start && d <= end).sort((a, b) => a - b);

    if (inWindow.length === 0) return { first: null, last: null };

    return {
      first: format(inWindow[0], 'yyyy-MM-dd'),
      last: format(inWindow[inWindow.length - 1], 'yyyy-MM-dd')
    };
  }

  const { first: windowFirstAvailable, last: windowLastAvailable } =
    findWindowFirstLastAvailable(icalAvailabilityStatus, baseMonth, 3);

  const renderCalendar = (monthOffset) => {
    const monthDate = addMonths(baseMonth, monthOffset);
    const allDates = Object.entries(icalAvailabilityStatus || {});
    const availableDates = allDates
      .filter(([, val]) => val.status === 'available')
      .map(([date]) => new Date(date));

    return (
      <div key={monthOffset} className="calendar-container">
        <h3>{format(monthDate, 'MMMM yyyy', { locale: de })}</h3>

        <DatePicker
          inline
          locale={de}
          calendarStartDay={1}
          openToDate={monthDate}
          highlightDates={[
            { 'react-datepicker__day--highlighted-custom-1': availableDates },
          ]}
          dayClassName={(date) => {
            const key = format(date, 'yyyy-MM-dd');
            const isInMonth = isSameMonth(date, monthDate);
            if (!isInMonth) return 'react-datepicker__day--disabled';

            if (rangeStarts.has(key) && rangeEnds.has(key)) return 'half-single-available';
            if (rangeStarts.has(key)) return 'half-first-available';
            if (rangeEnds.has(key)) return 'half-last-available';

            return icalAvailabilityStatus[key]?.status === 'available'
              ? 'highlighted-custom-1'
              : 'unavailable';
          }}
          renderCustomHeader={() => null}
        />
      </div>
    );
  };

  const showPagination =
    Object.keys(icalAvailabilityStatus).length > 0 && !previewData;

  return (
    <div className="settings-tab">
      <h2>{texts.availabilityIcal.title}</h2>

      {isLoading ? (
        <div>⏳ {texts.availabilityIcal.loading}</div>
      ) : (
        <>
          <div style={{ marginBottom: 12 }}>
            <input
              id="icalUrl"
              type="url"
              value={icalUrl}
              onChange={(e) => setIcalUrl(e.target.value)}
              placeholder={texts.availabilityIcal.placeholderUrl}
              style={{
                width: '100%',
                padding: 8,
                borderRadius: 6,
                border: '1px solid #ccc'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              className="legend-button"
              onClick={handleTestPreview}
              disabled={isSaving || !icalUrl || isTesting}
            >
              {isTesting
                ? texts.availabilityIcal.testing
                : texts.availabilityIcal.testPreview}
            </button>

            {originalUrl ? (
              <button
                type="button"
                className="legend-button"
                onClick={handleShowSynced}
                disabled={isLoading}
                style={{ marginLeft: 8 }}
              >
                {texts.availabilityIcal.showSynced}
              </button>
            ) : (
              <div style={{ width: 220 }} />
            )}

            <button
              type="button"
              className="legend-button"
              onClick={handleSave}
              disabled={isSaving || !icalUrl}
              style={{ marginLeft: 'auto' }}
            >
              {isSaving
                ? texts.availabilityIcal.saving
                : texts.availabilityIcal.save}
            </button>

            {originalUrl && (
              <button
                type="button"
                className="legend-button"
                onClick={handleRemove}
                style={{ marginLeft: 8 }}
              >
                {texts.availabilityIcal.remove}
              </button>
            )}
          </div>

          {lastSync && (
            <h2>
              <div style={{ marginTop: 12 }}>
                <strong>{texts.availabilityIcal.lastSync}:</strong>{' '}
                {formatLocalFromWallClock(lastSync)}
              </div>
            </h2>
          )}

          {previewError && (
            <div className="error">
              {texts.availabilityIcal.previewFailed}: {previewError}
            </div>
          )}

          {showPagination && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                marginTop: 12
              }}
            >
              <button
                type="button"
                onClick={handlePrev}
                className="legend-button"
              >
                ‹ {texts.availabilityIcal.prev}
              </button>

              <button
                type="button"
                onClick={handleNext}
                className="legend-button"
              >
                {texts.availabilityIcal.next} ›
              </button>

              <div
                style={{
                  marginLeft: 'auto',
                  fontSize: 13,
                  color: '#666'
                }}
              >
                {texts.availabilityIcal.previewCalendar}
              </div>
            </div>
          )}

          {Object.keys(icalAvailabilityStatus).length > 0 && (
            <div style={{ marginTop: 12 }} className="calendar-row">
              {[0, 1, 2].map((offset) => renderCalendar(offset))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
